import PortalSkeleton from "components/PortalLoading";
import { useState } from 'react';
import { FiArrowUpRight, FiBookOpen, FiChevronDown, FiSliders } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import useAuthStore from 'store/authStore';
import useMenuItems from 'layouts/Admin2/useMenuItems';
import ExpandableReport from './ExpandableReport';

const descriptions = {
    admin: 'A clear view of your library, readers, and revenue.',
    publisher: 'Keep your catalogue moving and your earnings in view.',
    institution: 'Manage your learning community and its library in one place.',
    ambassador: 'Follow your clients, commissions, and growing community.',
};
const shortcuts = {
    admin: ['/books/admin/all', '/users/admin/all', '/orders'],
    publisher: ['/books/admin/all', '/books/publisher/purchased', '/admin/geo-restrictions'],
    institution: ['/books/institution', '/books/institution-picks', '/subject-groups'],
    ambassador: ['/ba/client-payments'],
};

const workspaceNames = { admin: 'Admin overview', publisher: 'Publisher overview', institution: 'Institution overview', ambassador: 'Ambassador overview' };

export default function DashboardWelcome({ variant = 'admin' }) {
    const { userProfile } = useAuthStore();
    const navigation = useMenuItems();
    const available = navigation.reduce((all, item) => all.concat(item.children || [item]), []);
    const links = shortcuts[variant].map((path) => available.find((item) => item.to === path)).filter(Boolean);
    return <section className="portal-welcome" aria-labelledby="dashboard-heading">
        <div className="portal-welcome-copy"><span className="portal-eyebrow"><FiBookOpen aria-hidden="true" /> {workspaceNames[variant]}</span>
            <h1 id="dashboard-heading">Welcome back{userProfile?.first_name ? `, ${userProfile.first_name}` : ''}<span className="portal-welcome-period">.</span></h1>
            <p>{descriptions[variant]}</p>
        </div>
        {links.length > 0 && <nav className="portal-shortcuts" aria-label="Dashboard shortcuts">{links.map((item) => <Link key={item.to} to={item.to} className="portal-shortcut"><span className="portal-shortcut-icon" aria-hidden="true">{item.icon}</span><span>{item.label}</span><FiArrowUpRight aria-hidden="true" /></Link>)}</nav>}
    </section>;
}

export function DashboardQuickLinks() {
    const items = useMenuItems().flatMap((item) => item.children || [item]);
    const links = ['/users/admin/all', '/orders', '/vouchers', '/books/admin/genres-and-categories', '/publishers', '/institutions'].map((path) => items.find((item) => item.to === path)).filter(Boolean);
    if (!links.length) return null;
    return <ReportPanel title="Quick links" description="Common tasks across your workspace." className="portal-dashboard-span portal-dashboard-quicklinks">
        <nav className="portal-quick-link-grid" aria-label="Quick workspace links">{links.map((item) => <Link key={item.to} to={item.to}><span aria-hidden="true">{item.icon}</span><strong>{item.label}</strong><FiArrowUpRight aria-hidden="true" /></Link>)}</nav>
    </ReportPanel>;
}

export function MetricCard({ label, value, icon }) {
    return <div className="portal-metric"><span className="portal-metric-icon" aria-hidden="true">{icon}</span><div><p>{label}</p><strong>{value ?? '—'}</strong></div></div>;
}

let nextReportId = 0;

export function ReportPanel({ title, description, filters, result, children, className = '', skeleton = {} }) {
    const [open, setOpen] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [contentId] = useState(() => `portal-report-content-${++nextReportId}`);
    return <ExpandableReport title={title} collapsed={!open} className={`portal-report-panel ${className}${open ? '' : ' is-collapsed'}`} aria-busy={!!result?.isLoading} onExpand={() => setOpen(true)}>{({ controls }) => <>
        <header className="portal-report-heading portal-report-heading-collapsible">
            <div className="portal-report-heading-copy"><h2>{title}</h2>{description && <p>{description}</p>}</div>
            <div className="portal-report-heading-actions">
                {open && filters && <button type="button" className="portal-button portal-report-filter" aria-expanded={filtersOpen} aria-controls={`${contentId}-filters`} onClick={() => setFiltersOpen((value) => !value)}><FiSliders aria-hidden="true" /><span>Filters</span></button>}
                <button type="button" className="portal-report-toggle" aria-label={`${open ? 'Collapse' : 'Expand'} ${title.toLowerCase()}`} aria-expanded={open} aria-controls={contentId} onClick={() => setOpen((value) => !value)}><FiChevronDown aria-hidden="true" /></button>
                {controls}
            </div>
            {open && filters && <div id={`${contentId}-filters`} className="portal-report-controls" hidden={!filtersOpen}>{filters}</div>}
        </header>
        <div id={contentId} hidden={!open}>
            {result?.isLoading ? <PortalSkeleton {...skeleton} label={`Loading ${title.toLowerCase()}…`} /> : result?.isError ? <div className="portal-report-error" role="alert"><p>We couldn't load this report.</p><button type="button" className="portal-button" onClick={() => result.refetch()}>Try again</button></div> : children}
        </div>
    </>}</ExpandableReport>;
}
